import { Dispatch } from 'redux'

import * as ActiveSourceApi from 'redux/api/DataPlatform/connections/activeSources/v1'
import * as SourceApi from 'redux/api/DataPlatform/connections/sources/v1'
import {
  TActiveSourceBody,
  TResetPasswordBody
} from 'redux/api/DataPlatform/connections/activeSources/v1'

import { toggleSnackbarOpen } from '../../../Snackbar'
import { SnackbarMessage } from 'types/GlobalSnackbar'
import { useThunkActions } from 'redux/actions/useThunkActions'

import * as actions from './actions'
import * as sourceActions from '../Sources/actions'

export function tryCreateSFTPSettings(id: string) {
  return (dispatch: Dispatch) => {
    dispatch(actions.createSftpSettings())

    ActiveSourceApi.createSFTPSettings(id)
      .then((response) => {
        dispatch(
          actions.createSftpSettingsSuccess({
            ...response.data,
            activeSourceId: id
          })
        )

        dispatch(toggleSnackbarOpen(SnackbarMessage.Success))
      })
      .catch(() => {
        dispatch(actions.createSftpSettingsFailed())

        dispatch(toggleSnackbarOpen(SnackbarMessage.Failed))
      })
  }
}

export function tryCreateActiveSource(body: TActiveSourceBody) {
  return (dispatch: Dispatch) => {
    dispatch(actions.create())

    ActiveSourceApi.create(body)
      .then((response) => {
        dispatch(
          actions.createSuccess({
            ...response.data,
            activeSourceId: response.data.meta.active_source_id
          })
        )

        dispatch(toggleSnackbarOpen(SnackbarMessage.Success))
      })
      .catch(() => {
        dispatch(actions.createFailed())

        dispatch(toggleSnackbarOpen(SnackbarMessage.Failed))
      })
  }
}

export function tryUpdateActiveSource(
  sourceId: string,
  body: TActiveSourceBody
) {
  return (dispatch: Dispatch) => {
    dispatch(actions.update())

    ActiveSourceApi.update(sourceId, body)
      .then((response) => {
        dispatch(
          actions.updateSuccess({ ...response.data, activeSourceId: sourceId })
        )

        dispatch(toggleSnackbarOpen(SnackbarMessage.Success))
      })
      .catch(() => {
        dispatch(actions.updateFailed())

        dispatch(toggleSnackbarOpen(SnackbarMessage.Failed))
      })
  }
}

export function tryUploadActiveSourceFile(
  id: string,
  body: FormData,
  callback?: (err?: boolean) => void
) {
  return (dispatch: Dispatch) => {
    dispatch(actions.upload())

    ActiveSourceApi.upload(id, body)
      .then((response) => {
        dispatch(
          actions.uploadSuccess({ ...response.data, activeSourceId: id })
        )

        dispatch(toggleSnackbarOpen(SnackbarMessage.Success))

        if (callback) {
          callback()
        }
      })
      .catch(() => {
        dispatch(actions.uploadFailed())

        // The caller explains a structure mismatch itself — the generic
        // snackbar cannot say which file was wrong or why.
        if (callback) {
          callback(true)
        }
      })
  }
}

export function tryActivateActiveSource(id: string) {
  return (dispatch: Dispatch) => {
    dispatch(actions.activate())

    ActiveSourceApi.activate(id)
      .then(() => {
        dispatch(actions.activateSuccess({ id }))

        dispatch(toggleSnackbarOpen(SnackbarMessage.Success))
      })
      .catch(() => {
        dispatch(actions.activateFailed())

        dispatch(toggleSnackbarOpen(SnackbarMessage.Failed))
      })
  }
}

export function tryDeleteActiveSource(sourceId: string, id: string) {
  return (dispatch: Dispatch) => {
    dispatch(actions.destroy())

    ActiveSourceApi.remove(id)
      .then(() => {
        dispatch(actions.destroySuccess({ id, sourceId }))

        dispatch(toggleSnackbarOpen(SnackbarMessage.Success))
      })
      .catch(() => {
        dispatch(actions.destroyFailed())

        dispatch(toggleSnackbarOpen(SnackbarMessage.Failed))
      })
  }
}

export function tryResetPassword(id: string, body: TResetPasswordBody) {
  return (dispatch: Dispatch) => {
    dispatch(actions.resetPassword())

    ActiveSourceApi.resetPassword(id, body)
      .then((response) => {
        dispatch(
          actions.resetPasswordSuccess({ ...response.data, activeSourceId: id })
        )

        dispatch(toggleSnackbarOpen(SnackbarMessage.Success))
      })
      .catch(() => {
        dispatch(actions.resetPasswordFailed())

        dispatch(toggleSnackbarOpen(SnackbarMessage.Failed))
      })
  }
}

export function tryScanActiveSource(id: string, file?: File) {
  return (dispatch: Dispatch) => {
    ActiveSourceApi.scan(id, file)
      .then(() => {
        dispatch(toggleSnackbarOpen(SnackbarMessage.Success))

        if (file) {
          SourceApi.getOne(id).then((response) => {
            dispatch(
              sourceActions.getOneSuccess({
                ...response.data,
                activeSourceId: id
              })
            )
          })
        }
      })
      .catch(() => {
        dispatch(toggleSnackbarOpen(SnackbarMessage.Failed))
      })
  }
}

export const useActiveSourcesActions = () => {
  return {
    tryActivateActiveSource: useThunkActions(tryActivateActiveSource),
    tryCreateActiveSource: useThunkActions(tryCreateActiveSource),
    tryCreateSFTPSettings: useThunkActions(tryCreateSFTPSettings),
    tryDeleteActiveSource: useThunkActions(tryDeleteActiveSource),
    tryResetPassword: useThunkActions(tryResetPassword),
    tryUploadActiveSourceFile: useThunkActions(tryUploadActiveSourceFile),
    tryScanActiveSource: useThunkActions(tryScanActiveSource),
    tryUpdateActiveSource: useThunkActions(tryUpdateActiveSource)
  }
}
